/* Persona Shop All — Paper 01.G02 States & Logic.
   One active sport shared by the Find Your Sport tiles and the sticky sport
   bar that pins under the header once the tiles scroll away; product
   type and finish stack on top; sort reorders without changing the set.
   Everything re-filters in place (200ms fade), the count follows, the URL
   keeps the view shareable, and ALL is the only sport reset. */
(() => {
  const FADE = 200;
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class Shop {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-ps-shop-grid]');
      this.items = [...this.grid.querySelectorAll('.ps-shop__item')];
      this.byKey = new Map(this.items.map((i) => [i.dataset.key, i]));
      this.breaks = [...this.grid.querySelectorAll('[data-ps-break]')];
      this.chips = [...root.querySelectorAll('[data-ps-sport-chip]')];
      this.sbar = root.querySelector('[data-ps-sbar]');
      this.sbarBar = root.querySelector('[data-ps-sbar-bar]');
      this.sbarStrip = root.querySelector('[data-ps-sbar-chips]');
      this.sbarCount = root.querySelector('[data-ps-sbar-count]');
      this.tiles = [...document.querySelectorAll('[data-ps-sport-tile]')];
      this.more = document.querySelector('[data-ps-shop-more]');
      this.empty = root.querySelector('[data-ps-empty]');
      this.count = root.querySelector('[data-ps-shop-count]');
      this.filterChips = root.querySelector('[data-ps-chips]');
      this.chipList = root.querySelector('[data-ps-chip-list]');
      this.scrim = root.querySelector('[data-ps-scrim]');
      this.labels = {};
      root.querySelectorAll('[data-ps-f]').forEach((i) => { this.labels[i.value] = i.parentElement.textContent.trim(); });

      const q = new URLSearchParams(location.search);
      const valid = (type, v) => !!root.querySelector(`[data-ps-f="${type}"][value="${v}"]`);
      const serverSort = q.get('sort_by');
      this.state = {
        sport: q.get('sport') && this.chips.concat(this.tiles).some((t) => (t.dataset.psSportChip ?? t.dataset.psSportTile) === q.get('sport')) ? q.get('sport') : '',
        type: new Set((q.get('type') || '').split(',').filter((v) => valid('type', v))),
        finish: new Set((q.get('finish') || '').split(',').filter((v) => valid('finish', v))),
        sort: serverSort && root.querySelector(`[data-ps-sort="${serverSort}"]`) ? serverSort : 'featured',
        pages: 1,
      };
      this.orders = { [this.state.sort]: this.items.map((i) => i.dataset.key) };

      // a tile or a chip: filter, then bring the top of the products into view
      this.tiles.forEach((t) => t.addEventListener('click', () => {
        this.setSport(t.dataset.psSportTile);
        this.scrollToGrid();
      }));
      this.chips.forEach((c) => c.addEventListener('click', () => {
        this.setSport(c.dataset.psSportChip);
        this.scrollToGrid();
      }));
      this.initSportBar();
      root.querySelectorAll('[data-ps-f]').forEach((i) => i.addEventListener('change', () => {
        const set = this.state[i.dataset.psF];
        i.checked ? set.add(i.value) : set.delete(i.value);
        this.update();
      }));
      root.querySelectorAll('[data-ps-clear]').forEach((b) => b.addEventListener('click', () => {
        this.state.type.clear();
        this.state.finish.clear();
        this.closeSheets();
        this.update();
      }));
      root.querySelectorAll('[data-ps-sort]').forEach((b) => b.addEventListener('click', () => this.setSort(b.dataset.psSort)));
      this.initDropdowns();
      this.initSheets();
      this.more?.querySelector('[data-ps-more]')?.addEventListener('click', () => {
        const before = this.visible().length ? this.shown : 0;
        this.state.pages += 1;
        this.render(false);
        const next = this.ordered().filter((i) => !i.hidden)[before];
        next?.querySelector('a')?.focus({ preventScroll: true });
      });
      desktop.addEventListener('change', () => this.render(false));
      this.render(false);
    }

    /* ---------------------------------------------------------- state */
    scrollToGrid() {
      // the sticky bar will be showing once we land, so clear it as well as the header
      const offset = this.headerH() + (this.sbarBar ? this.sbarBar.offsetHeight : 0);
      const target = this.root.querySelector('.ps-shop__wrap') || this.root;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: reduced() ? 'auto' : 'smooth' });
    }

    /* ---------------------------------------------------------- sticky sport bar */
    headerH() {
      const header = document.querySelector('.ps-hdr-section');
      if (!header) return 0;
      const pos = getComputedStyle(header).position;
      return pos === 'sticky' || pos === 'fixed' ? header.getBoundingClientRect().height : 0;
    }

    initSportBar() {
      if (!this.sbar) return;
      const tiles = document.querySelector('[data-ps-stiles]');
      // past = the tiles have gone up under the header + bar; inShop = the catalogue still reaches below it
      this.sbarState = { past: !tiles, inShop: false };
      const observe = () => {
        this.sbarIO?.disconnect();
        const h = this.headerH();
        this.sbar.style.setProperty('--ps-sbar-top', `${h}px`);
        const edge = Math.round(h + (this.sbarBar.offsetHeight || 57));
        this.sbarIO = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            const above = e.boundingClientRect.bottom <= edge;
            if (e.target === tiles) this.sbarState.past = !e.isIntersecting && above;
            else this.sbarState.inShop = e.isIntersecting || !above;
          });
          this.sbarState.inShop = this.sbarState.inShop && this.root.getBoundingClientRect().bottom > edge;
          this.toggleSportBar(this.sbarState.past && this.sbarState.inShop);
        }, { rootMargin: `-${edge}px 0px 0px 0px`, threshold: 0 });
        if (tiles) this.sbarIO.observe(tiles);
        this.sbarIO.observe(this.root);
      };
      observe();
      const header = document.querySelector('.ps-hdr-section');
      if (header && window.ResizeObserver) {
        let last = this.headerH();
        new ResizeObserver(() => { const h = this.headerH(); if (h !== last) { last = h; observe(); } }).observe(header);
      }

      // toolbar keyboard: one tab stop, arrows / Home / End move between chips
      this.sbarStrip.addEventListener('keydown', (e) => {
        const i = this.chips.indexOf(document.activeElement);
        if (i < 0) return;
        const n = this.chips.length;
        const to = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: n - 1 }[e.key];
        if (to === undefined) return;
        e.preventDefault();
        const c = this.chips[(to + n) % n];
        this.chips.forEach((x) => { x.tabIndex = x === c ? 0 : -1; });
        c.focus();
        this.revealChip(c);
      });
    }

    toggleSportBar(on) {
      if (this.sbarOn === on) return;
      this.sbarOn = on;
      this.sbarBar.classList.toggle('is-on', on);
      if (!on && this.sbarBar.contains(document.activeElement)) document.activeElement.blur();
      if (on) this.revealChip(this.chips.find((c) => c.getAttribute('aria-pressed') === 'true'));
    }

    // keep a chip inside the swipe strip without moving the page
    revealChip(c) {
      const strip = this.sbarStrip;
      if (!c || !strip || strip.scrollWidth <= strip.clientWidth) return;
      const pad = 16;
      const l = c.offsetLeft - strip.offsetLeft;
      const r = l + c.offsetWidth;
      // fully visible (clear of the right-edge fade)? leave it; else snap it to the left edge
      if (l - pad >= strip.scrollLeft && r + 56 <= strip.scrollLeft + strip.clientWidth) return;
      const x = l - pad;
      strip.scrollTo({ left: Math.max(0, x), behavior: reduced() ? 'auto' : 'smooth' });
    }

    setSport(sport) {
      if (sport === this.state.sport) return;
      this.state.sport = sport;
      this.update();
    }

    async setSort(sort) {
      this.state.sort = sort;
      this.root.querySelectorAll('[data-ps-sort]').forEach((b) => b.setAttribute('aria-selected', b.dataset.psSort === sort ? 'true' : 'false'));
      const cur = this.root.querySelector(`.ps-shop__opt[data-ps-sort="${sort}"]`);
      const lab = this.root.querySelector('[data-ps-sort-cur]');
      if (cur && lab) lab.textContent = cur.textContent.trim();
      this.closeDropdowns();
      this.closeSheets();
      await this.ensureOrder(sort);
      this.update();
    }

    async ensureOrder(sort) {
      if (this.orders[sort]) return;
      const price = (i) => parseInt(i.dataset.price, 10) || 0;
      const base = this.orders.featured || Object.values(this.orders)[0];
      if (sort === 'price-ascending' || sort === 'price-descending') {
        const list = base.map((k) => this.byKey.get(k));
        list.sort((a, b) => (sort === 'price-ascending' ? price(a) - price(b) : price(b) - price(a)));
        this.orders[sort] = list.map((i) => i.dataset.key);
        return;
      }
      try {
        const url = new URL(location.pathname, location.origin);
        url.searchParams.set('section_id', this.root.dataset.section);
        if (sort !== 'featured') url.searchParams.set('sort_by', sort);
        const html = await (await fetch(url)).text();
        const keys = [...html.matchAll(/data-key="(\d+)"/g)].map((m) => m[1]).filter((k) => this.byKey.has(k));
        const rest = [...this.byKey.keys()].filter((k) => !keys.includes(k));
        this.orders[sort] = [...new Set(keys)].concat(rest);
      } catch (e) {
        this.orders[sort] = base;
      }
    }

    update() {
      this.state.pages = 1;
      this.syncUrl();
      this.render(true);
    }

    matches(item, ignoreFilters) {
      const { sport, type, finish } = this.state;
      if (sport && !item.dataset.sports.includes(` ${sport} `)) return false;
      if (ignoreFilters) return true;
      if (type.size && !type.has(item.dataset.type)) return false;
      if (finish.size && !finish.has(item.dataset.finish)) return false;
      return true;
    }

    ordered() {
      const keys = this.orders[this.state.sort] || this.orders.featured || Object.values(this.orders)[0];
      return keys.map((k) => this.byKey.get(k));
    }

    visible() { return this.ordered().filter((i) => this.matches(i)); }

    syncUrl() {
      const url = new URL(location.href);
      const set = (k, v) => (v ? url.searchParams.set(k, v) : url.searchParams.delete(k));
      set('sport', this.state.sport);
      set('type', [...this.state.type].join(','));
      set('finish', [...this.state.finish].join(','));
      set('sort_by', this.state.sort === 'featured' ? '' : this.state.sort);
      history.replaceState(history.state, '', url);
    }

    /* ---------------------------------------------------------- render */
    render(animate) {
      const apply = () => {
        const d = desktop.matches;
        const size = parseInt(d ? this.root.dataset.pageD : this.root.dataset.pageM, 10) || 20;
        const vis = this.visible();
        const limit = size * this.state.pages;
        this.shown = Math.min(limit, vis.length);
        this.items.forEach((i) => { i.hidden = true; });
        vis.forEach((i, idx) => {
          i.hidden = idx >= limit;
          i.style.order = idx * 2;
        });
        const cols = d ? 4 : 2;
        this.breaks.forEach((b) => {
          const after = (parseInt(d ? b.dataset.afterD : b.dataset.afterM, 10) || 2) * cols;
          b.hidden = this.shown <= after;
          b.style.order = after * 2 - 1;
        });
        this.empty.hidden = vis.length > 0;
        this.count.textContent = this.root.dataset.countTpl.replace('[count]', vis.length);
        if (this.sbarCount) this.sbarCount.textContent = this.count.textContent;

        // one active sport, two renderings (tiles + sticky bar chips)
        this.chips.forEach((c) => {
          const on = c.dataset.psSportChip === this.state.sport;
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
          c.tabIndex = on ? 0 : -1;
        });
        this.revealChip(this.chips.find((c) => c.dataset.psSportChip === this.state.sport));
        this.tiles.forEach((t) => t.setAttribute('aria-pressed', t.dataset.psSportTile === this.state.sport ? 'true' : 'false'));
        document.querySelectorAll('[data-ps-count-for]').forEach((c) => {
          const h = c.dataset.psCountFor;
          const n = this.items.filter((i) => i.dataset.sports.includes(` ${h} `)).length;
          c.textContent = c.dataset.tpl.replace('[count]', n);
        });

        this.root.querySelectorAll('[data-ps-f]').forEach((i) => { i.checked = this.state[i.dataset.psF].has(i.value); });
        const active = [...this.state.type].map((v) => ['type', v]).concat([...this.state.finish].map((v) => ['finish', v]));
        this.filterChips.hidden = !active.length;
        this.chipList.replaceChildren(...active.map(([k, v]) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ps-shop__chip';
          b.textContent = this.labels[v] || v;
          const x = document.createElement('span');
          x.textContent = '×';
          x.setAttribute('aria-hidden', 'true');
          b.append(x);
          b.setAttribute('aria-label', `Remove ${this.labels[v] || v}`);
          b.addEventListener('click', () => { this.state[k].delete(v); this.update(); });
          return b;
        }));

        if (this.more) {
          this.more.hidden = vis.length === 0;
          this.more.querySelector('[data-ps-more-label]').textContent = this.more.dataset.tpl.replace('[shown]', this.shown).replace('[total]', vis.length);
          this.more.querySelector('[data-ps-more-fill]').style.width = `${vis.length ? (this.shown / vis.length) * 100 : 0}%`;
          this.more.querySelector('[data-ps-more]').hidden = this.shown >= vis.length;
        }
      };
      if (!animate || reduced()) return apply();
      this.grid.classList.add('is-fading');
      setTimeout(() => { apply(); this.grid.classList.remove('is-fading'); }, FADE);
    }

    /* ---------------------------------------------------------- desktop dropdowns */
    initDropdowns() {
      this.dds = [...this.root.querySelectorAll('[data-ps-dd]')];
      this.dds.forEach((dd) => {
        const btn = dd.querySelector('.ps-shop__dd-btn');
        const panel = dd.querySelector('.ps-shop__panel');
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') !== 'true';
          this.closeDropdowns();
          btn.setAttribute('aria-expanded', open);
          panel.hidden = !open;
        });
      });
      document.addEventListener('click', (e) => { if (!e.target.closest('[data-ps-dd]')) this.closeDropdowns(); });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openBtn = this.root.querySelector('.ps-shop__dd-btn[aria-expanded="true"]');
        this.closeDropdowns();
        openBtn?.focus();
        if (this.openSheet) this.closeSheets();
      });
    }

    closeDropdowns() {
      this.dds?.forEach((dd) => {
        dd.querySelector('.ps-shop__dd-btn').setAttribute('aria-expanded', 'false');
        dd.querySelector('.ps-shop__panel').hidden = true;
      });
    }

    /* ---------------------------------------------------------- mobile sheets */
    initSheets() {
      this.root.querySelectorAll('[data-ps-sheet-open]').forEach((b) => b.addEventListener('click', () => this.openSheetFor(b.dataset.psSheetOpen, b)));
      this.root.querySelectorAll('[data-ps-sheet-close]').forEach((b) => b.addEventListener('click', () => this.closeSheets()));
      this.scrim?.addEventListener('click', () => this.closeSheets());
      this.root.querySelector('[data-ps-apply]')?.addEventListener('click', () => {
        ['type', 'finish'].forEach((k) => {
          this.state[k] = new Set([...this.root.querySelectorAll(`[data-ps-sf="${k}"]:checked`)].map((i) => i.value));
        });
        this.closeSheets();
        this.update();
      });
    }

    openSheetFor(name, opener) {
      const sheet = this.root.querySelector(`[data-ps-sheet="${name}"]`);
      if (!sheet) return;
      this.root.querySelectorAll('[data-ps-sf]').forEach((i) => { i.checked = this.state[i.dataset.psSf].has(i.value); });
      this.opener = opener;
      this.openSheet = sheet;
      this.scrim.hidden = false;
      sheet.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      sheet.querySelector('input, [data-ps-sort], button')?.focus();
    }

    closeSheets() {
      if (!this.openSheet) return;
      this.openSheet.hidden = true;
      this.scrim.hidden = true;
      this.openSheet = null;
      document.documentElement.style.overflow = '';
      this.opener?.focus({ preventScroll: true });
    }
  }

  const boot = () => document.querySelectorAll('[data-ps-shop]').forEach((el) => {
    if (!el.psShop) el.psShop = new Shop(el);
  });
  boot();
  document.addEventListener('shopify:section:load', boot);
})();
